import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Phone, ShoppingCart, Tag, type LucideIcon } from 'lucide-react';
import Logo from './Logo';
import SiteBanner from './SiteBanner';
import { COMPANY } from '@/lib/data';
import { useContainerPrices } from '@/lib/useContainerPrices';

interface NavChild {
  label: string;
  to: string;
}

interface NavItem {
  label: string;
  to: string;
  children?: NavChild[];
  icon?: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Priser', to: '/priser' },
  {
    label: 'Tjenester',
    to: '/tjenester',
    children: [
      { label: 'Privat', to: '/tjenester/privat' },
      { label: 'Bedrift & borettslag', to: '/tjenester/bedrift-borettslag' },
      { label: 'Rehab & riving', to: '/tjenester/rehab-riving' },
      { label: 'Storsekker', to: '/tjenester/storsekker' },
    ],
  },
  { label: 'Fraksjoner', to: '/fraksjoner' },
  { label: 'Utstyr', to: '/utstyr' },
  { label: 'Om oss', to: '/om-oss' },
  { label: 'Nyheter', to: '/nyheter' },
  { label: 'Kontakt', to: '/kontakt' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { hasActiveCampaigns } = useContainerPrices();

  const navItems: NavItem[] = hasActiveCampaigns
    ? [{ label: 'Tilbud', to: '/tilbud', icon: Tag }, ...NAV_ITEMS]
    : NAV_ITEMS;

  return (
    <header className="sticky top-0 z-50 border-b border-bmg-gray-border bg-bmg-white">
      {/* Top bar */}
      <div className="bg-bmg-ink text-bmg-white text-sm">
        <div className="container-bmg flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-bmg-yellow" />
            <a href={`tel:${COMPANY.phoneRaw}`} className="font-medium hover:text-bmg-yellow transition-colors">
              {COMPANY.phone}
            </a>
            <span aria-hidden="true" className="mx-2 text-bmg-ink-muted">|</span>
            <span className="hidden sm:inline text-bmg-ink-muted">{COMPANY.hours}</span>
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-bmg-yellow transition-colors">
              Facebook
            </a>
            <Link to="/kunder" className="hover:text-bmg-yellow transition-colors">
              Referanser
            </Link>
            <Link to="/miljo" className="hover:text-bmg-yellow transition-colors">
              Miljø
            </Link>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="container-bmg flex items-center justify-between py-3">
        <Link to="/" className="flex-shrink-0">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded transition-colors ${
                      isActive
                        ? 'text-bmg-ink bg-bmg-yellow-light'
                        : 'text-bmg-ink-soft hover:text-bmg-ink'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
                {servicesOpen && (
                  <div className="absolute left-0 top-full pt-1 z-50">
                    <div className="bg-bmg-white border border-bmg-gray-border rounded-lg shadow-lg py-2 min-w-[220px]">
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="block px-4 py-2 text-sm text-bmg-ink-soft hover:bg-bmg-gray hover:text-bmg-ink transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded transition-colors ${
                    isActive
                      ? 'text-bmg-ink bg-bmg-yellow-light'
                      : 'text-bmg-ink-soft hover:text-bmg-ink'
                  }`
                }
              >
                {item.icon ? <item.icon className="h-4 w-4 inline mr-1" /> : null}
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/bestill" className="btn-primary text-sm hidden sm:inline-flex">
            <ShoppingCart className="h-4 w-4" />
            Bestill container
          </Link>
          <Link to="/bestill" className="btn-primary sm:hidden px-3 py-1.5 text-sm gap-1.5" aria-label="Bestill container">
            <ShoppingCart className="h-4 w-4" />
            Bestill
          </Link>
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Meny"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-bmg-gray-border bg-bmg-white animate-fade-in">
          <nav className="container-bmg py-4 flex flex-col gap-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <Link
                    to={item.to}
                    className="block px-3 py-3 text-sm font-medium text-bmg-ink-soft hover:text-bmg-ink"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                  <div className="pl-4">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="block px-3 py-3 text-sm text-bmg-ink-soft hover:text-bmg-ink"
                        onClick={() => setMobileOpen(false)}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className="block px-3 py-3 text-sm font-medium text-bmg-ink-soft hover:text-bmg-ink"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              )
            )}
            <Link
              to="/bestill"
              className="btn-primary mt-2"
              onClick={() => setMobileOpen(false)}
            >
              <ShoppingCart className="h-4 w-4" />
              Bestill container
            </Link>
          </nav>
        </div>
      )}

      <SiteBanner hidden={mobileOpen} />
    </header>
  );
}
